// Establishments.tsx
//
// As empresas vêm do `decryptedInfoToken`, não de HTTP — mesmo caminho que
// `Subsidiary.tsx` já segue, e pelo mesmo motivo: o token de organização já
// traz a travessia organization -> companies -> subsidiaries inteira, com
// `companyName` e as filiais junto, que é exatamente o que esta tela desenha.
//
// O que estava aqui antes era `setEstablishments([...])` com "Pizzaria Gourmet"
// e "Pizzaria BBB" escritos à mão. A tela de Empresa do dono da Famiglia Lucco
// mostrava duas pizzarias que não existem — e a chamada real estava comentada
// logo acima do mock.
import React, { useState, useMemo } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/storesSection/Company";
import { useCombinedStore } from "../../../../store";

interface EstablishmentsProps {
  decryptedInfoToken: any;
  handleOpenModal: (type: string, object?: any) => void;
}

const Establishments: React.FC<EstablishmentsProps> = ({
  decryptedInfoToken,
  handleOpenModal,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const { authUserZustand } = useCombinedStore((state) => ({
    authUserZustand: state.authUserZustand,
  }));

  // Travessia organization -> companies. O hook do token pode devolver null no
  // primeiro render, então tudo com `?.`.
  const establishments: any[] = useMemo(
    () => decryptedInfoToken?.organization?.companies ?? [],
    [decryptedInfoToken]
  );

  const isLoading = !decryptedInfoToken;

  // Filtrar estabelecimentos
  const filteredEstablishments = establishments.filter((establishment) => {
    // `companyDescription` não existe no dado real — era campo do mock.
    const matchesSearch =
      establishment.companyName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      establishment.companyTaxPayerID?.includes(searchTerm);

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && establishment.companyStatus) ||
      (statusFilter === "inactive" && !establishment.companyStatus);

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: boolean) => (
    <AlfredoStyles.StatusBadge $isActive={status}>
      <AlfredoStyles.MaterialIcons>
        {status ? "check_circle" : "cancel"}
      </AlfredoStyles.MaterialIcons>
      {status ? "Ativa" : "Inativa"}
    </AlfredoStyles.StatusBadge>
  );

  const getSubsidiariesCount = (establishment: any) => (
    <AlfredoStyles.SubsidiaryBadge>
      <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-md)" }}>
        store
      </AlfredoStyles.MaterialIcons>
      {establishment.subsidiaries?.length || 0} loja
      {establishment.subsidiaries?.length !== 1 ? "s" : ""}
    </AlfredoStyles.SubsidiaryBadge>
  );

  const getCompanyLogo = (logoUrl: string) => {
    if (logoUrl && logoUrl.startsWith("http")) {
      return logoUrl;
    } else if (logoUrl && logoUrl.startsWith("data:")) {
      return logoUrl;
    } else if (logoUrl) {
      return `data:image/jpeg;base64,${logoUrl}`;
    }
    return null;
  };

  return (
    <AlfredoStyles.FieldsContainer>
      {/* Cabeçalho */}
      <AlfredoStyles.Header>
        <AlfredoStyles.HeaderContent>
          <AlfredoStyles.HeaderTitle>
            <AlfredoStyles.MaterialIcons style={{ fontSize: "calc(var(--u) * 36)" }}>
              business
            </AlfredoStyles.MaterialIcons>
            Estabelecimentos
          </AlfredoStyles.HeaderTitle>
          <AlfredoStyles.HeaderSubtitle>
            Gerencie suas marcas e redes de lojas
          </AlfredoStyles.HeaderSubtitle>
        </AlfredoStyles.HeaderContent>

        <AlfredoStyles.Button onClick={() => handleOpenModal("company")}>
          <AlfredoStyles.MaterialIcons>add</AlfredoStyles.MaterialIcons>
          Novo Estabelecimento
        </AlfredoStyles.Button>
      </AlfredoStyles.Header>

      {/* Filtros e Busca */}
      <AlfredoStyles.FilterSection>
        <AlfredoStyles.SearchWrapper>
          <AlfredoStyles.SearchIcon>
            <AlfredoStyles.MaterialIcons>search</AlfredoStyles.MaterialIcons>
          </AlfredoStyles.SearchIcon>
          <AlfredoStyles.SearchInput
            type="text"
            placeholder="Buscar por nome, CNPJ ou descrição..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <AlfredoStyles.ClearButton onClick={() => setSearchTerm("")}>
              <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
            </AlfredoStyles.ClearButton>
          )}
        </AlfredoStyles.SearchWrapper>

        <AlfredoStyles.FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Todos os status</option>
          <option value="active">Ativas</option>
          <option value="inactive">Inativas</option>
        </AlfredoStyles.FilterSelect>
      </AlfredoStyles.FilterSection>

      {/* Estatísticas */}
      <AlfredoStyles.StatsGrid>
        <AlfredoStyles.StatCard $color="#0066cc">
          <AlfredoStyles.StatIcon>🏢</AlfredoStyles.StatIcon>
          <AlfredoStyles.StatValue>
            {establishments.length}
          </AlfredoStyles.StatValue>
          <AlfredoStyles.StatLabel>
            Total de Estabelecimentos
          </AlfredoStyles.StatLabel>
        </AlfredoStyles.StatCard>

        <AlfredoStyles.StatCard $color="#28a745">
          <AlfredoStyles.StatIcon>✅</AlfredoStyles.StatIcon>
          <AlfredoStyles.StatValue>
            {establishments.filter((e) => e.companyStatus).length}
          </AlfredoStyles.StatValue>
          <AlfredoStyles.StatLabel>
            Estabelecimentos Ativos
          </AlfredoStyles.StatLabel>
        </AlfredoStyles.StatCard>

        <AlfredoStyles.StatCard $color="#ffc107">
          <AlfredoStyles.StatIcon>🏪</AlfredoStyles.StatIcon>
          <AlfredoStyles.StatValue>
            {establishments.reduce(
              (acc, establishment) =>
                acc + (establishment.subsidiaries?.length || 0),
              0
            )}
          </AlfredoStyles.StatValue>
          <AlfredoStyles.StatLabel>Total de Lojas</AlfredoStyles.StatLabel>
        </AlfredoStyles.StatCard>
      </AlfredoStyles.StatsGrid>

      {/* Lista de Estabelecimentos */}
      {isLoading ? (
        <AlfredoStyles.LoadingContainer>
          <AlfredoStyles.LoadingSpinner />
          <div>Carregando estabelecimentos...</div>
        </AlfredoStyles.LoadingContainer>
      ) : filteredEstablishments.length > 0 ? (
        <AlfredoStyles.EstablishmentsList>
          {filteredEstablishments.map((establishment) => (
            <AlfredoStyles.EstablishmentCard
              key={establishment.companyUuid}
              onClick={() => handleOpenModal("company", establishment)}
            >
              <AlfredoStyles.CardHeader>
                <AlfredoStyles.CardHeaderLeft>
                  {getCompanyLogo(establishment.companyLogoUrl) ? (
                    <AlfredoStyles.CompanyLogo
                      src={getCompanyLogo(establishment.companyLogoUrl)!}
                      alt={establishment.companyName}
                    />
                  ) : (
                    <AlfredoStyles.CompanyLogoPlaceholder>
                      <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-4xl)" }}>
                        business
                      </AlfredoStyles.MaterialIcons>
                    </AlfredoStyles.CompanyLogoPlaceholder>
                  )}

                  <AlfredoStyles.CardInfo>
                    <AlfredoStyles.CardTitleRow>
                      <AlfredoStyles.CardTitle>
                        {establishment.companyName}
                      </AlfredoStyles.CardTitle>
                      {getStatusBadge(establishment.companyStatus)}
                      {getSubsidiariesCount(establishment)}
                    </AlfredoStyles.CardTitleRow>

                    {/*
                      Não existe `companyDescription` em `acd."ActCompany"`
                      (conferido coluna a coluna): era campo do mock. Renderizar
                      "Sem descrição" para todo mundo é ruído — a tela esconde a
                      linha em vez de mostrar um vazio permanente, mesmo critério
                      que `Subsidiary.tsx` usa para o endereço ilegível.
                    */}
                    <AlfredoStyles.CardMeta>
                      <AlfredoStyles.MetaItem>
                        <AlfredoStyles.MaterialIcons
                          style={{ fontSize: "var(--fs-base)" }}
                        >
                          badge
                        </AlfredoStyles.MaterialIcons>
                        <strong>CNPJ:</strong> {establishment.companyTaxPayerID}
                      </AlfredoStyles.MetaItem>
                    </AlfredoStyles.CardMeta>
                  </AlfredoStyles.CardInfo>
                </AlfredoStyles.CardHeaderLeft>

                <AlfredoStyles.CardActions>
                  <AlfredoStyles.Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenModal("company", establishment);
                    }}
                  >
                    <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                      edit
                    </AlfredoStyles.MaterialIcons>
                    Editar
                  </AlfredoStyles.Button>
                  <AlfredoStyles.SecondaryButton
                    onClick={(e) => {
                      e.stopPropagation();
                      // Navegar para lojas deste estabelecimento
                    }}
                  >
                    <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                      store
                    </AlfredoStyles.MaterialIcons>
                    Ver Lojas
                  </AlfredoStyles.SecondaryButton>
                </AlfredoStyles.CardActions>
              </AlfredoStyles.CardHeader>

              {/* Lojas do Estabelecimento */}
              {establishment.subsidiaries &&
                establishment.subsidiaries.length > 0 && (
                  <AlfredoStyles.SubsidiariesSection>
                    <AlfredoStyles.SubsidiariesHeader>
                      <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                        store
                      </AlfredoStyles.MaterialIcons>
                      <span>Lojas vinculadas:</span>
                    </AlfredoStyles.SubsidiariesHeader>
                    <AlfredoStyles.SubsidiariesList>
                      {establishment.subsidiaries.map((subsidiary: any) => (
                        <AlfredoStyles.SubsidiaryTag
                          key={subsidiary.subsidiaryUuid}
                        >
                          <AlfredoStyles.MaterialIcons
                            style={{ fontSize: "var(--fs-xs)" }}
                          >
                            {subsidiary.subsidiaryStatus
                              ? "check_circle"
                              : "cancel"}
                          </AlfredoStyles.MaterialIcons>
                          {subsidiary.subsidiaryName}
                        </AlfredoStyles.SubsidiaryTag>
                      ))}
                    </AlfredoStyles.SubsidiariesList>
                  </AlfredoStyles.SubsidiariesSection>
                )}
            </AlfredoStyles.EstablishmentCard>
          ))}
        </AlfredoStyles.EstablishmentsList>
      ) : (
        <AlfredoStyles.EmptyState>
          <AlfredoStyles.EmptyStateIcon>🏢</AlfredoStyles.EmptyStateIcon>
          <AlfredoStyles.EmptyStateTitle>
            {searchTerm
              ? "Nenhum estabelecimento encontrado"
              : "Nenhum estabelecimento cadastrado"}
          </AlfredoStyles.EmptyStateTitle>
          <AlfredoStyles.EmptyStateDescription>
            {searchTerm
              ? "Tente ajustar os filtros de busca"
              : "Comece criando seu primeiro estabelecimento"}
          </AlfredoStyles.EmptyStateDescription>
          {!searchTerm && (
            <AlfredoStyles.Button
              onClick={() => handleOpenModal("company")}
              style={{ marginTop: "var(--sp-16)" }}
            >
              <AlfredoStyles.MaterialIcons>add</AlfredoStyles.MaterialIcons>
              Criar Primeiro Estabelecimento
            </AlfredoStyles.Button>
          )}
        </AlfredoStyles.EmptyState>
      )}

      {/* Contador de resultados */}
      {!isLoading && filteredEstablishments.length > 0 && (
        <AlfredoStyles.ResultsCounter>
          Mostrando <strong>{filteredEstablishments.length}</strong> de{" "}
          <strong>{establishments.length}</strong> estabelecimento
          {establishments.length !== 1 ? "s" : ""}
        </AlfredoStyles.ResultsCounter>
      )}
    </AlfredoStyles.FieldsContainer>
  );
};

export default Establishments;
